// Central config: translations (swappable), feature flags, scoring constants.
// Adding a licensed translation later is a config change here + a data source,
// never a rewrite. `premium: true` gates it behind an Apple in-app purchase.

export interface TranslationDef {
  code: string
  name: string
  shortName: string
  publicDomain: boolean
  premium: boolean
  /** REST endpoint template; {ref} is URL-encoded passage, e.g. "John 3:16". */
  apiTemplate?: string
}

export const TRANSLATIONS: Record<string, TranslationDef> = {
  BSB: {
    code: 'BSB',
    name: 'Berean Standard Bible',
    shortName: 'BSB',
    publicDomain: true,
    premium: false,
    // NOTE: bible-api.com does NOT serve the BSB (its translations are web, kjv,
    // etc.). The chapter reader (lib/bible.ts) reads unsupported translations in
    // WEB instead. Our quiz text is the bundled BSB (data/bible/pool.ts).
    apiTemplate: 'https://bible-api.com/{ref}?translation=web',
  },
  WEB: {
    code: 'WEB',
    name: 'World English Bible',
    shortName: 'WEB',
    publicDomain: true,
    premium: false,
    apiTemplate: 'https://bible-api.com/{ref}?translation=web',
  },
  // Premium slots — wired for the future, gated behind IAP, no live source yet.
  ESV: { code: 'ESV', name: 'English Standard Version', shortName: 'ESV', publicDomain: false, premium: true },
  NLT: { code: 'NLT', name: 'New Living Translation', shortName: 'NLT', publicDomain: false, premium: true },
  CSB: { code: 'CSB', name: 'Christian Standard Bible', shortName: 'CSB', publicDomain: false, premium: true },
}

export const DEFAULT_TRANSLATION =
  import.meta.env.VITE_DEFAULT_TRANSLATION || 'BSB'

// Reading translations (which Bible a verse is SHOWN in) live in
// lib/translations.ts, served by API.Bible and bible-api.com through lib/bible.ts.

// Feature flags — flip these to stage rollouts.
export const FEATURES = {
  groups: true,
  ambientPresence: true,
  collectibles: true,
  dailyReward: true,
  shareCard: true,
}

// Scoring — tuned so speed AND accuracy matter, but accuracy dominates.
export const SCORING = {
  basePerCorrect: 100,
  // Speed bonus decays over the answer window; fast but not frantic.
  maxSpeedBonus: 100,
  // 16.5s — 10% slower than the original 15s window, so the clock feels less
  // frantic. Drives the countdown bar, the auto-miss timeout, and the speed
  // bonus decay in scoreQuestion(), which all read this one value.
  answerWindowMs: 16500,
  // Combo multiplier grows with consecutive correct answers (arcade juice).
  comboStep: 0.25, // +25% per combo level
  comboMax: 2.5,
}

export const isSupabaseConfigured = Boolean(
  import.meta.env.VITE_SUPABASE_URL && import.meta.env.VITE_SUPABASE_ANON_KEY,
)

// Optional "support / founding patron" payment link — a Stripe Payment Link URL
// (or any checkout URL). Kept as a swappable env value so the receiving Stripe
// account can be changed without a code change or redeploy from us; the support
// button is hidden entirely when this is unset. On web it opens in a new tab;
// note that for NATIVE app-store builds, digital-cosmetic purchases must go
// through in-app purchase — this external link is web-only.
export const SUPPORT_URL = import.meta.env.VITE_SUPPORT_URL || ''

// Per-skin checkout links (Stripe Payment Links — public, shareable URLs, not
// secrets). Env vars override the defaults so a link can be swapped without a
// code change. Falls back to SUPPORT_URL, then ''.
//
// ONE ENTRY, and that is the whole shop now. Cosmetics are no longer sold:
// Moses, Esther and Elijah are free, the angels are road rewards, and the
// founding patron is the only thing left with a price on it. Their old
// Payment Links are deleted rather than commented out — a live link with no
// product behind it is a way to take somebody's money for nothing.
//
// A Payment Link is a price, not a product: what a payment GRANTS is decided
// by `client_reference_id` ("<username>-<skinId>", see lib/checkout) and
// resolved by fulfill_skin, so ANY link the Stripe account owns settles as
// 'cephas' — the webhook is on the account, not the link. The whale's original
// link (`…9B6fZh1Uk1lHdzC2Wda3u06`) carried the patron through the swap for
// exactly that reason; it was replaced on 2026-09-03 with a link made for the
// founding patron, so the checkout page names what is being bought without a
// dashboard rename. If the old link is still live in Stripe it will still
// fulfil correctly, but deactivate it: two live links at one price is a page
// somebody can bookmark and be sold a product this app no longer offers under
// that name.
//
// The old `whale` key is gone with the retirement. Nothing asks for it — the
// support card asks for PATRON_SKU and the Skins grid never opens a checkout
// for a retired skin — and leaving a live $9.99 link keyed to a withdrawn
// product is exactly the "take somebody's money for nothing" case above.
export const SKIN_BUY_URLS: Record<string, string> = {
  cephas: import.meta.env.VITE_BUY_CEPHAS || 'https://buy.stripe.com/9B66oHcyY2pLdzC8gxa3u07',
}

export const skinBuyUrl = (id: string): string => SKIN_BUY_URLS[id] || SUPPORT_URL || ''

// Per-BUNDLE checkout links (see data/avatar BUNDLES). Empty, because BUNDLES
// is: the Angel Pack's skins are road rewards now. Kept, along with its lookup,
// so selling a pack again is a row here rather than a rebuilt code path. No
// SUPPORT_URL fallback, then or now — a pack must never quietly check out at a
// different price.
export const BUNDLE_BUY_URLS: Record<string, string> = {}

export const bundleBuyUrl = (id: string): string => BUNDLE_BUY_URLS[id] || ''

// Web Push (VAPID). This PUBLIC key is safe to ship — it's how the browser
// authenticates our push server. The matching PRIVATE key lives only as a
// Supabase Edge Function secret (VAPID_PRIVATE_KEY) and is never in the client.
// Overridable via env so the pair can be rotated without a code change.
export const VAPID_PUBLIC_KEY =
  import.meta.env.VITE_VAPID_PUBLIC_KEY ||
  'BMjBZZ-dKxm_J-ARiuH3TPX1kjGbR4Uju1JtgZbsdp1P0phqRTiwKiEARyqOD_R0AkYhEDCcAX0tRWkkyvzHW1c'
